import { Box, Grid, useMediaQuery } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react';
import GoodCategoryService from '../../../shared/services/good-category/good-category.service';
import { IGoodCategory, IPostGoodCategory } from '../../../shared/services/good-category/good-category.model';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { useLocation, useSearchParams } from 'react-router-dom';
import { LoadingPage } from '../loading-page';
import { deleteQueryParamFromUrl } from '../../../shared/utils/misc-shared-utils';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { GoodCategoryDialog } from '../../dialogs/settings/good-category-dialog';
import { GoodCategoryImportDialog } from '../../dialogs/settings/good-category-import-dialog';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../hooks/use-debounce-hook';

export const SettingsGoodCategoriesPage = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  //Services
  const goodCategoryService = new GoodCategoryService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [categories, setCategories] = useState<IGoodCategory[]>()
  const [goodCategoryId, setGoodCategoryId] = useState<string>()
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [openImportDialog, setOpenImportDialog] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState('')
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    if (searchParams.has('openDialog')) {
      if (searchParams.get('openDialog') === 'true') {
        setOpenDialog(true);
        deleteQueryParamFromUrl(searchParams, 'openDialog')
      }
      deleteQueryParamFromUrl(searchParams, 'openDialog')
    }
  }, [location.search]);

  useEffect(() => {
    getAllGoodCategories()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, debouncedSearchQuery])

  const getAllGoodCategories = () => {
    requestId.current++
    const currentRequestId = requestId.current
    goodCategoryService.getAllWithPagination(debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setCategories(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'status', numeric: false, label: '', sortable: true },
    { id: 'category', numeric: false, label: 'Category', sortable: true, isHeading: true },
    { id: 'subCategoryOne', numeric: false, label: 'Subcategory One', sortable: true },
    { id: 'subCategoryTwo', numeric: false, label: 'Subcategory Two', sortable: true },
    { id: 'goodCategoryReference', numeric: false, label: 'Reference(s)', sortable: true },
  ];

  const rows = !categories ? [] : categories.map((category) => {
    return {
      id: category.id,
      action: category.organisationId !== null ? (
        <TableActionButtons
          id={category.id}
          includeDelete={true}
          includeEdit={true}
          onEdit={(id: string) => editClickHandler(id)}
          onDelete={(id: string) => deleteClickHandler(id, category.isInUse)}
        />
      ) : null,
      category: category.category,
      subCategoryOne: category.subCategoryOne,
      subCategoryTwo: category.subCategoryTwo,
      goodCategoryReference: category.goodCategoryReference ? category.goodCategoryReference.split(',').join(', ') : null,
      // status: category.organisationId !== null ? (<Chip color='primary' variant='outlined' label={'Custom'} />) : null,
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (goodCategoryId: string) => {
    setGoodCategoryId(goodCategoryId)
    setOpenDialog(true)
  }

  const deleteClickHandler = (goodCategoryId: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this product category as it has or currently is in use. Please review and update any associated products before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Confirmation',
        message: `Are you sure you want to delete this product category? This action cannot be undone.`,
        buttonCopy: 'Continue',
        cancelButtonCopy: 'Cancel'
      })
        .then(response => {
          if (response) {
            goodCategoryService.delete(goodCategoryId)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                getAllGoodCategories()
              }).catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
              })
          }
        })
    }
  }

  //Dialog Event Handlers
  const submitDialogHandler = (values: IPostGoodCategory, resetForm: () => void, isEdit: boolean,) => {
    if (!isEdit) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        goodCategoryService.create(values)
          .then(response => {
            getAllGoodCategories()
            resetForm();
            setOpenDialog(false)
            userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.code === 1) {
              userNotificationsContext.openDialog({
                active: true, title: 'Duplicated Category',
                message: `A category with the same name and subcategories already exists. Please use a unique combination or modify the existing entry.`,
                buttonCopy: 'Ok',
                hideCancelButton: true
              })
            } else if (err.response.data.code === 2) {
              userNotificationsContext.openDialog({
                active: true, title: 'Action Not Allowed',
                message: `Subcategory 2 cannot exist without Subcategory 1. Please enter Subcategory 1 first.`,
                buttonCopy: 'Ok',
                hideCancelButton: true
              })
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
              resetForm()
              setOpenDialog(false)
            }
            setIsDialogSubmitting(false)
          })
      }
    }
    if (isEdit && goodCategoryId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        goodCategoryService.edit(goodCategoryId, values)
          .then(response => {
            getAllGoodCategories()
            resetForm();
            setOpenDialog(false)
            setGoodCategoryId('')
            userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.code === 1) {
              userNotificationsContext.openDialog({
                active: true, title: 'Duplicated Category',
                message: `A category with the same name and subcategories already exists. Please use a unique combination or modify the existing entry.`,
                buttonCopy: 'Ok',
                hideCancelButton: true
              })
            } else if (err.response.data.code === 2) {
              userNotificationsContext.openDialog({
                active: true, title: 'Action Not Allowed',
                message: `Subcategory 2 cannot exist without Subcategory 1. Please enter Subcategory 1 first.`,
                buttonCopy: 'Ok',
                hideCancelButton: true
              })
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
              resetForm()
              setOpenDialog(false)
            }
            setIsDialogSubmitting(false)
          })
      }
    }
  }

  const closeDialogHandler = () => {
    setOpenDialog(false)
    setGoodCategoryId('')
  }

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <PageHeading
        title='Product Categories'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setOpenDialog(true) }}
        actionButtonText='Add Category'
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => { setOpenImportDialog(true) }}
        secondaryActionButtonText='Import'
        hasTabs={false}
      />
      <Grid container sx={{ mb: 2 }}>
        <Grid
          item
          xl={4} lg={4} md={6} sm={12} xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            width: '100%'
          }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </Grid>
      </Grid>
      <LoadingPage pageReady={!isLoading}>
        {categories && <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />}
      </LoadingPage>
      {!goodCategoryId && openDialog &&
        <GoodCategoryDialog
          onSubmit={submitDialogHandler}
          open={openDialog}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          isEdit={false} />}
      {goodCategoryId && openDialog &&
        <GoodCategoryDialog
          isSubmitting={isDialogSubmitting}
          onSubmit={submitDialogHandler}
          open={openDialog}
          closeHandler={closeDialogHandler}
          isEdit={true}
          goodCategoryId={goodCategoryId} />}
      {openImportDialog &&
        <GoodCategoryImportDialog
          open={openImportDialog}
          closeHandler={() => setOpenImportDialog(false)}
          onImported={() => getAllGoodCategories()} />}
    </PageOuterContainer>
  )
}